import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, renderSlot as _renderSlot, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "mutate-entity-modal-body" }
const _hoisted_2 = {
  key: 1,
  class: "loading apollo"
}

import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import enUK from "@/locales/invoicing/en-UK.json";

import FormItem from "@/components/form/FormItem.vue";
import Modal from "@/components/Modal.vue";
import BasicButton from "@/components/BasicButton.vue";
import AddOffered from "@/components/offered/AddOffered.vue";

import { CREATE_INVOICE } from "@/graphql/invoice/mutations";
import { useToast } from "vue-toastification";
import { useMutation, useQuery, useResult } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import dayjs from "dayjs";
import { DatePickerMarker } from "@vuepic/vue-datepicker";
import {
  GET_PROJECT_COMPANIES,
  GET_CLIENTS_TO_INVOICE,
} from "@/graphql/invoice/queries";
import { GET_UNINVOICED_TIMESHEET_ENTRIES } from "@/graphql/timesheets/queries";

type MessageSchema = typeof enUK;


export default /*@__PURE__*/_defineComponent({
  __name: 'AddInvoice',
  props: {
  itemToInvoice: {
    type: Object,
    required: true,
  },
  upUntil: {
    type: String,
    required: false,
  },
},
  emits: ["close"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

const toast = useToast();

console.log(JSON.stringify(props.itemToInvoice, null, 2));

const {
  result: timesheetEntriesResult,
  loading: loadingTimesheetEntries,
  onError: onErrorTimesheetEntries,
} = useQuery(GET_UNINVOICED_TIMESHEET_ENTRIES, () => ({
  projectId: props.itemToInvoice.projectId,
  upUntil: props.upUntil,
}));

const uninvoicedTimesheetEntries = computed(() => {
  const entries = timesheetEntriesResult.value?.uninvoicedTimesheetEntries;
  if (!Array.isArray(entries)) return [];
  return entries.map((entry) => ({
    id: entry.id,
    date: entry.date,
    duration: entry.duration,
    projectId: entry.theme.project.id,
    themeId: entry.theme.id,
    notes: entry.notes,
    invoiceId: entry.invoiceId,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
    userId: entry.user.id,
    projectRoleId: entry.role.id,
    billable: entry.billable,
    synced: entry.synced,
    active: entry.active,
  }));
});

onErrorTimesheetEntries((error) => {
  logErrorMessages(error);
  toast.error(t("errors.getTimesheetEntries"));
});

const {
  result,
  loading: loadingCompanies,
  onError: onErrorCompanies,
} = useQuery(GET_PROJECT_COMPANIES, () => ({
  id: props.itemToInvoice.projectId,
}));

const companies = useResult(
  result,
  null,
  (result) => result?.project?.client?.companies,
);

onErrorCompanies((error) => {
  logErrorMessages(error);
  toast.error(t("errors.getCompanies"));
});

const showDetailsOnInvoice = ref(true); // TODO Emiref, persistence ?
const showOfferedOnInvoice = ref(false);
const companyId = ref("");
const dueDate = ref(
  props.itemToInvoice.draftInvoice
    ? props.itemToInvoice.draftInvoice.dueDate
    : dayjs().add(10, "day").format("YYYY-MM-DD"),
);

const variables = reactive({
  companyId,
  showDetailsOnInvoice,
  showOfferedOnInvoice,
  dueDate,
});
const v$ = useVuelidate(
  {
    companyId: required,
    dueDate: required,
    showDetailsOnInvoice: {},
    showOfferedOnInvoice: {},
  },
  variables,
  {
    $autoDirty: true,
  },
);

const clientsToInvoiceRefetchQueries = computed(() => [
  {
    query: GET_CLIENTS_TO_INVOICE,
    variables: { upUntil: props.upUntil, filterBalance: "positive" },
  },
  {
    query: GET_CLIENTS_TO_INVOICE,
    variables: { upUntil: props.upUntil, filterBalance: "negative" },
  },
]);

const {
  mutate: createInvoice,
  onDone,
  onError,
  loading: createInvoiceLoading,
} = useMutation(CREATE_INVOICE, () => ({
  variables: {
    companyId: companyId.value,
    projectId: props.itemToInvoice.projectId,
    dueDate: dueDate.value,
    uninvoicedTimesheetEntries: uninvoicedTimesheetEntries.value,
    showDetailsOnInvoice: showDetailsOnInvoice.value,
    showOfferedOnInvoice: showOfferedOnInvoice.value,
    upUntil: props.upUntil,
  },
  refetchQueries: clientsToInvoiceRefetchQueries.value,
  awaitRefetchQueries: true,
}));

onDone(async (result) => {
  console.log(JSON.stringify(result.data, null, 2));
  if (result.data?.createInvoiceForTimesheets?.status === "ok") {
    toast.success(t("success.createInvoice"));
    emit("close");
  } else {
    const errorResponse = result.data?.createInvoiceForTimesheets?.error;
    console.log(errorResponse);
    toast.error(t("errors.createInvoice", { message: errorResponse }));
  }
});

onError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.createInvoice", { message: t("errors.internal") }));
});

const submitForm = async () => {
  const isFormCorrect = await v$.value.$validate();
  if (isFormCorrect) {
    return createInvoice();
  }
};

const closeModal = () => {
  emit("close");
};

const dateMarkers: DatePickerMarker[] = [
  {
    date: dayjs().add(10, "days").toDate(),
    type: "line",
    tooltip: [{ text: t("labels.defaultDueDate"), color: "blue" }],
  },
];

watch(
  // Set default company as first in the list
  companies,
  (newCompanies) => {
    if (props.itemToInvoice.draftInvoice) {
      companyId.value = props.itemToInvoice.draftInvoice.company.id;
    } else if (newCompanies?.length > 0 && !companyId.value) {
      companyId.value = newCompanies[0].id;
    }
  },
  { immediate: true },
);

const loading = computed(() => createInvoiceLoading.value);
const isAddOfferedModalVisible = ref(false);

const showAddOfferedModal = () => {
  isAddOfferedModalVisible.value = true;
};
const closeAddOfferedModal = () => {
  isAddOfferedModalVisible.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Modal, { onClose: closeModal }, {
      header: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)("titles.edit")), 1)
      ]),
      body: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          (_unref(companies))
            ? (_openBlock(), _createElementBlock("form", {
                key: 0,
                id: `mutate-form-edit-delta`,
                onSubmit: _withModifiers(submitForm, ["prevent"])
              }, [
                _createVNode(FormItem, {
                  input: companyId.value,
                  "onUpdate:input": _cache[0] || (_cache[0] = ($event: any) => ((companyId).value = $event)),
                  label: _unref(t)(`labels.companyId`),
                  options: _unref(companies),
                  name: "companyId",
                  errors: _unref(v$).companyId.$errors
                }, null, 8, ["input", "label", "options", "errors"]),
                _createVNode(FormItem, {
                  "date-markers": dateMarkers,
                  input: dueDate.value,
                  "onUpdate:input": _cache[1] || (_cache[1] = ($event: any) => ((dueDate).value = $event)),
                  label: _unref(t)(`labels.dueDate`),
                  name: "dueDate",
                  "input-type": "date",
                  errors: _unref(v$).dueDate.$errors
                }, null, 8, ["input", "label", "errors"]),
                _createVNode(FormItem, {
                  input: showDetailsOnInvoice.value,
                  "onUpdate:input": _cache[2] || (_cache[2] = ($event: any) => ((showDetailsOnInvoice).value = $event)),
                  label: _unref(t)(`labels.showDetailsOnInvoice`),
                  name: "showDetailsOnInvoice",
                  errors: _unref(v$).showDetailsOnInvoice.$errors,
                  "input-type": "checkbox"
                }, null, 8, ["input", "label", "errors"]),
                _createVNode(FormItem, {
                  input: showOfferedOnInvoice.value,
                  "onUpdate:input": _cache[3] || (_cache[3] = ($event: any) => ((showOfferedOnInvoice).value = $event)),
                  label: _unref(t)(`labels.showOfferedOnInvoice`),
                  name: "showOfferedOnInvoice",
                  errors: _unref(v$).showOfferedOnInvoice.$errors,
                  "input-type": "checkbox"
                }, null, 8, ["input", "label", "errors"])
              ], 32))
            : _createCommentVNode("", true),
          (loading.value || _unref(loadingCompanies) || _unref(loadingTimesheetEntries))
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_unref(t)("loading")), 1))
            : _createCommentVNode("", true)
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(BasicButton, {
          class: "mutate-button",
          type: "submit",
          color: "primary",
          form: `mutate-form-edit-delta`,
          disabled: loading.value
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("buttons.createInvoice.now")), 1)
          ]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(BasicButton, {
          class: "mutate-button",
          type: "button",
          color: "primary",
          disabled: loading.value,
          onClick: showAddOfferedModal
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("buttons.offered.offerAmount")), 1)
          ]),
          _: 1
        }, 8, ["disabled"]),
        _renderSlot(_ctx.$slots, "footer")
      ]),
      _: 3
    }),
    (isAddOfferedModalVisible.value)
      ? (_openBlock(), _createBlock(AddOffered, {
          key: 0,
          onClose: closeAddOfferedModal,
          "project-id": props.itemToInvoice.projectId,
          "refetch-queries": clientsToInvoiceRefetchQueries.value
        }, null, 8, ["project-id", "refetch-queries"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})